import React, { createContext, useContext, useMemo, useState } from "react";

import {
    TournamentContextProps,
    TournamentContextValues,
} from "../types/contexts";
import { PhotoData } from "../types/tournament";

import { isChildTournament } from "../../../../../lib/utils";
import {
    IGame,
    IPlayoffBracket,
    ITournamentSchedule,
} from "../../../../../types/api";
import { AsyncStatuses } from "../../../../../types/shared";

const TournamentContext = createContext<TournamentContextValues | null>(null);

export const useTournamentContext = () => {
    const context = useContext(TournamentContext);
    if (!context) {
        throw new Error(
            "useTournamentContext must be used within a TournamentProvider",
        );
    }
    return context;
};

export const TournamentProvider: React.FC<TournamentContextProps> = props => {
    const {
        children,
        methods,
        tournament,
        isCreatingTournament,
        tournamentDirectors,
        teams,
    } = props;

    const isMasterTournament = !isChildTournament(tournament);

    const [logo, setLogo] = useState<File[]>([]);
    const [photos, setPhotos] = useState<File[]>([]);
    const [showBracketChangeModal, setShowBracketChangeModal] =
        useState<boolean>(false);
    const [modalOldBracketGames, setModalOldBracketGames] = useState<
        Array<IGame>
    >([]);
    const [modalDroppedGames, setModalDroppedGames] = useState<Array<number>>(
        [],
    );
    const [modalDropGamesCount, setModalDropGamesCount] = useState<number>(0);
    const [modalChangedAgeLevel, setModalChangedAgeLevel] =
        useState<string>("");
    const [currentPhotos, setCurrentPhotos] = useState<PhotoData[] | null>(
        isMasterTournament
            ? [...(tournament?.photos || [])].sort((a, b) => a.order - b.order)
            : [],
    );
    const [fetchStatus, setFetchStatus] = useState<AsyncStatuses>(undefined);

    const contextValue = useMemo(
        () => ({
            methods,
            tournament,
            isCreatingTournament,
            tournamentDirectors,
            teams,
            logo,
            setLogo,
            photos,
            setPhotos,
            currentPhotos,
            setCurrentPhotos,
            showBracketChangeModal,
            setShowBracketChangeModal,
            modalOldBracketGames,
            setModalOldBracketGames,
            modalDroppedGames,
            setModalDroppedGames,
            modalDropGamesCount,
            setModalDropGamesCount,
            fetchStatus,
            setFetchStatus,
            modalChangedAgeLevel,
            setModalChangedAgeLevel,
        }),
        [
            teams,
            logo,
            photos,
            currentPhotos,
            showBracketChangeModal,
            modalOldBracketGames,
            modalDroppedGames,
            modalDropGamesCount,
            fetchStatus,
            modalChangedAgeLevel,
        ],
    );

    return (
        <TournamentContext.Provider value={contextValue}>
            {children}
        </TournamentContext.Provider>
    );
};
